import { useEffect, useState } from 'react';
import { iosInstallInstructions, isIosUserAgent } from '@/lib/installPlatform';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const NATIVE_DISMISS_KEY = 'carvoeiro-nav-install';
const IOS_HINT_KEY = 'carvoeiro-nav.ios-hint-dismissed';

function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function macTouch(): boolean {
  return 'ontouchend' in document;
}

export function InstallPrompt() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [hidden, setHidden] = useState(() => localStorage.getItem(NATIVE_DISMISS_KEY) === '1');
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);

    const ua = navigator.userAgent;
    if (isIosUserAgent(ua, macTouch()) && localStorage.getItem(IOS_HINT_KEY) !== '1') {
      setShowIosHint(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  if (event && !hidden) {
    return (
      <div className="install-prompt" role="dialog" aria-label="Install Carvoeiro Nav">
        <p>
          <strong>Install Carvoeiro Nav</strong>
          <span> Add it to your home screen for one-tap access.</span>
        </p>
        <div className="install-prompt__actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              localStorage.setItem(NATIVE_DISMISS_KEY, '1');
              setHidden(true);
            }}
          >
            Not now
          </button>
          <button
            type="button"
            className="btn"
            onClick={async () => {
              await event.prompt();
              localStorage.setItem(NATIVE_DISMISS_KEY, '1');
              setHidden(true);
            }}
          >
            Install
          </button>
        </div>
      </div>
    );
  }

  if (showIosHint) {
    return (
      <div className="install-prompt" role="dialog" aria-label="Add to Home Screen">
        <p>
          <strong>Add to Home Screen</strong>
          <span> {iosInstallInstructions(navigator.userAgent, macTouch())}</span>
        </p>
        <div className="install-prompt__actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              localStorage.setItem(IOS_HINT_KEY, '1');
              setShowIosHint(false);
            }}
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  return null;
}
